// Track 3.1 — Aero coach trigger hook.
//
// Subscribes to the vision pipeline's CoachEventBus. Each event becomes a
// candidate proactive ask to Aero AI; rate limits drop the noise. Surviving
// events are sent as `{type: "proactive", event, context, sensors}` over the
// existing Aero AI WebSocket — the engine answers with `event` mode (one
// sentence) and the response streams back into the same observations feed
// that the chat box already renders.
//
// Why a separate hook from useAeroAi? Two consumers, two cadences:
//   - useAeroAi handles user-typed asks. Cadence ∝ user.
//   - useAeroCoachTriggers handles pipeline-emitted events. Cadence ∝ rider
//     state, bounded by rate limits.
// Sharing the same WebSocket would race on the response stream; cleaner to
// open a second connection dedicated to proactive coaching.

import { useEffect, useRef } from 'react';
import { CoachEvent, CoachEventBus, CoachEventType } from '../vision/coachEvents';
import { AeroAiContext, AeroAiSensors } from './useAeroAi';

interface Options {
  wsUrl: string;
  userId?: string;
  sessionId?: string;
  bus: CoachEventBus;
  /** Pulled per-event so the engine sees the rider's numbers *now*, not
   *  when the event fired (transitions resolve in <200ms; the read is
   *  consistent enough). */
  getContext?: () => AeroAiContext | undefined;
  getSensors?: () => AeroAiSensors | undefined;
  /** Public vs Rapha picks the voice / model tier downstream. The engine
   *  doesn't branch on this for `event` mode itself — Track 3.3 does. */
  mode?: 'default' | 'rapha_event';
  locale?: string;
  /** Called with each assistant message as it streams in. The caller pipes
   *  these into the AeroAiPanel's observations feed alongside user-asked
   *  responses. */
  onObservation?: (msg: { id: string; content: string; streaming: boolean; trigger: CoachEventType }) => void;
  enabled?: boolean;
}

// Rate-limit windows. The plan calls for "1 per 20s overall, 1 per trigger
// type per 60s" — these are the floors the engine enforces; voice can layer
// its own gating on top.
const OVERALL_MIN_GAP_MS = 20_000;
const PER_TYPE_MIN_GAP_MS = 60_000;

// best_cda is the one event the rider *expects* immediate feedback on —
// allow it to override the overall gate so a sub-best lock isn't silent.
const OVERRIDE_OVERALL: Set<CoachEventType> = new Set<CoachEventType>([
  'best_cda',
]);

export function useAeroCoachTriggers(opts: Options): void {
  const {
    wsUrl,
    userId = '',
    sessionId = '',
    bus,
    mode = 'default',
    locale,
    enabled = true,
  } = opts;

  // Closure refs so a new context/onObservation each render doesn't tear
  // down the WS.
  const getContextRef = useRef(opts.getContext);
  const getSensorsRef = useRef(opts.getSensors);
  const onObservationRef = useRef(opts.onObservation);
  useEffect(() => {
    getContextRef.current = opts.getContext;
    getSensorsRef.current = opts.getSensors;
    onObservationRef.current = opts.onObservation;
  }, [opts.getContext, opts.getSensors, opts.onObservation]);

  const wsRef = useRef<WebSocket | null>(null);
  const lastEmitAtRef = useRef<number>(0);
  const lastEmitByTypeRef = useRef<Map<CoachEventType, number>>(new Map());
  // Stream state — track the id of the currently-streaming proactive msg so
  // tokens accumulate into the right card.
  const currentTriggerRef = useRef<CoachEventType | null>(null);
  const currentIdRef = useRef<string | null>(null);
  const currentContentRef = useRef<string>('');

  useEffect(() => {
    if (!enabled) return;
    let closed = false;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    const connect = () => {
      if (closed) return;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === 'token') {
            const tid = currentTriggerRef.current;
            const id = currentIdRef.current;
            if (tid && id) {
              currentContentRef.current += msg.content as string;
              onObservationRef.current?.({
                id,
                content: currentContentRef.current,
                streaming: true,
                trigger: tid,
              });
            }
          } else if (msg.type === 'done' || msg.type === 'error') {
            const tid = currentTriggerRef.current;
            const id = currentIdRef.current;
            if (tid && id) {
              onObservationRef.current?.({
                id,
                content: currentContentRef.current,
                streaming: false,
                trigger: tid,
              });
            }
            currentTriggerRef.current = null;
            currentIdRef.current = null;
            currentContentRef.current = '';
          }
        } catch (err) {
          console.warn('Coach trigger socket parse error', err);
        }
      };

      ws.onclose = () => {
        if (!closed) reconnectTimer = setTimeout(connect, 2_000);
      };
      ws.onerror = () => {
        // soft-fail; the rider can still use the typed Aero AI chat
      };
    };

    connect();
    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [enabled, wsUrl]);

  // Subscribe to the bus once per `bus` identity. The bus is stable for the
  // pipeline's lifetime so this effect runs once per mount.
  useEffect(() => {
    if (!enabled) return;
    const unsubscribe = bus.subscribe((event: CoachEvent) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;

      const overall = event.t - lastEmitAtRef.current;
      const perType =
        event.t - (lastEmitByTypeRef.current.get(event.type) ?? -Infinity);
      const overrideOverall = OVERRIDE_OVERALL.has(event.type);

      if (!overrideOverall && overall < OVERALL_MIN_GAP_MS) return;
      if (perType < PER_TYPE_MIN_GAP_MS) return;

      // Drop if a prior trigger is still streaming — overlapping coach
      // beats are confusing and contend for the voice channel downstream.
      if (currentTriggerRef.current != null) return;

      lastEmitAtRef.current = event.t;
      lastEmitByTypeRef.current.set(event.type, event.t);
      const id = `c-${event.t}-${event.type}`;
      currentTriggerRef.current = event.type;
      currentIdRef.current = id;
      currentContentRef.current = '';

      // Prime the observations feed with an empty streaming card so the UI
      // shows the trigger immediately while the model thinks.
      onObservationRef.current?.({
        id,
        content: '',
        streaming: true,
        trigger: event.type,
      });

      ws.send(
        JSON.stringify({
          type: 'proactive',
          event: { type: event.type, data: event.data },
          user_id: userId,
          session_id: sessionId,
          context: getContextRef.current?.(),
          sensors: getSensorsRef.current?.(),
          mode,
          locale,
        }),
      );
    });
    return unsubscribe;
  }, [bus, enabled, userId, sessionId, mode, locale]);
}
